import { getDefaultClient } from '@dynamic-labs-sdk/client';
import { assertDefined } from '@dynamic-labs-sdk/client/core';
import type { WalletConnectConnectionResult } from '@dynamic-labs-sdk/wallet-connect';
import {
  getSignClient,
  importWalletConnectSession,
} from '@dynamic-labs-sdk/wallet-connect/core';

import { solanaApiChainName, solanaMethods } from '../consts';
import { createWalletProviderFromWalletConnectSolanaSession } from '../createWalletProviderFromWalletConnectSolanaSession';

type ConnectWithWalletConnectSolanaParams = {
  /**
   * Whether this session's wallet accounts should be added to the dynamic's wallet accounts.
   */
  addToDynamicWalletAccounts?: boolean;
};

export const connectWithWalletConnectSolana = async (
  {
    addToDynamicWalletAccounts = true,
  }: ConnectWithWalletConnectSolanaParams = {},
  client = getDefaultClient()
): Promise<WalletConnectConnectionResult> => {
  const signClient = await getSignClient(client);

  assertDefined(client.projectSettings, 'Project settings unavailable');

  const solanaNetworks = client.projectSettings.networks?.find(
    (network) => network.chainName === solanaApiChainName
  )?.networks;

  assertDefined(solanaNetworks, 'Solana networks not found');

  /**
   * By inspecting the official reown Solana demo, we can see that the genesis hash is used as the chain id.
   * https://lab.reown.com/appkit/?name=multichain-all
   */
  const walletConnectChains = solanaNetworks.map(
    (network) => `solana:${network.genesisHash}`
  );

  /**
   * A WalletConnect connection request generates a URI and a promise.
   *
   * The URI must be handed to an external wallet application that will use it
   * to establish a connection via a WalletConnect relay server. Typically, this
   * is done by scanning a QR code or deep linking to the wallet application.
   *
   * The promise will resolve when the connection is established.
   */
  const { approval, uri } = await signClient.connect({
    optionalNamespaces: {
      solana: {
        chains: walletConnectChains,
        events: ['accountsChanged', 'chainChanged', 'disconnected'],
        methods: solanaMethods as unknown as string[],
      },
    },
  });

  assertDefined(
    uri,
    'Failed to establish WalletConnect session: no URI returned'
  );

  /**
   * Once the approval promise resolves, the WalletConnect session is established
   * between the wallet application and the dapp (this app).
   */
  const walletAccountsPromise = approval().then(async (session) => {
    assertDefined(
      session.namespaces.solana,
      `WalletConnect session did not establish a Solana connection. Found instead: ${Object.keys(
        session.namespaces
      ).join(', ')}`
    );

    return importWalletConnectSession(
      {
        addToDynamicWalletAccounts,
        sessionTopic: session.topic,
        walletProviderBuilder:
          createWalletProviderFromWalletConnectSolanaSession,
      },
      client
    );
  });

  return { approval: () => walletAccountsPromise, uri };
};
